import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

export interface SmartListFilter {
  type?: string
  category?: string
  account?: string
  dateRange?: string
  minAmount?: number
  maxAmount?: number
  tags?: string[]
  payee?: string
  searchTerm?: string
}

export interface SmartList {
  id: string
  user_id: string
  name: string
  filters: SmartListFilter
  icon?: string
  color?: string
  created_at: string
  updated_at: string
}

interface SmartListsState {
  smartLists: SmartList[]
  isLoading: boolean
  error: string | null
}

interface SmartListsStore extends SmartListsState {
  fetchSmartLists: (userId: string) => Promise<SmartList[]>
  addSmartList: (userId: string, name: string, filters: SmartListFilter, icon?: string, color?: string) => Promise<SmartList>
  updateSmartList: (smartListId: string, updates: Partial<SmartList>) => Promise<void>
  deleteSmartList: (smartListId: string) => Promise<void>
}

export const useSmartListsStore = create<SmartListsStore>((set, get) => ({
  smartLists: [],
  isLoading: false,
  error: null,

  fetchSmartLists: async (userId: string) => {
    try {
      set({ isLoading: true, error: null })

      const { data, error } = await supabase
        .from('smart_lists')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) throw error

      set({ smartLists: data || [], isLoading: false })
      return data || []
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch smart lists'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      return []
    }
  },

  addSmartList: async (userId: string, name: string, filters: SmartListFilter, icon?: string, color?: string) => {
    try {
      set({ isLoading: true, error: null })

      const newSmartList = {
        user_id: userId,
        name,
        filters,
        icon,
        color,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('smart_lists')
        .insert([newSmartList])
        .select()
        .single()

      if (error) throw error

      set(state => ({
        smartLists: [data, ...state.smartLists],
        isLoading: false
      }))

      toast.success('Smart list created successfully')
      return data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create smart list'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  updateSmartList: async (smartListId: string, updates: Partial<SmartList>) => {
    try {
      set({ isLoading: true, error: null })

      const { error } = await supabase
        .from('smart_lists')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', smartListId)

      if (error) throw error

      set(state => ({
        smartLists: state.smartLists.map(list => 
          list.id === smartListId ? { ...list, ...updates, updated_at: new Date().toISOString() } : list
        ),
        isLoading: false
      }))

      toast.success('Smart list updated successfully')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update smart list'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  deleteSmartList: async (smartListId: string) => {
    try {
      set({ isLoading: true, error: null })

      const { error } = await supabase
        .from('smart_lists')
        .delete()
        .eq('id', smartListId)

      if (error) throw error

      set(state => ({
        smartLists: state.smartLists.filter(list => list.id !== smartListId),
        isLoading: false
      }))

      toast.success('Smart list deleted successfully')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete smart list'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  }
})) 